/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import { useHistory } from "react-router-dom";
import { Button, Form, InputGroup } from "react-bootstrap";
import { getAuthSearchData, getSearchData } from "./redux/action";
import useDebounce from "./useDebounce";
import { CLEAR_SEARCH_DATA, SET_SEARCH_VAUE } from "./redux/constants";
import { getSearchList, getSearchLoader } from "./redux/selectors";
import { getUserInfo } from "../../store/selector/userSelector";
import DropdownBlogListTitle from "./DropdownBlogListTitle";

function SearchFeature() {
  const dispatch = useDispatch();
  const [showNoResult, setShowNoResult] = useState(false);
  const loader = useSelector(getSearchLoader);
  const [show, setShow] = useState(false);
  const pathname = useMemo(() => {
    return window.location.pathname;
  }, []);
  const inputRef = useRef<any>(null);
  const defaultSiteUrl = window.location.origin;
  const searchList = useSelector(getSearchList);
  const [value, setValue] = useState<any>("");
  const [showScroll, setShowScroll] = useState(false);
  const params = new URLSearchParams(document.location.search.substring(1));
  const searchValue = params.get("searchKeyword");
  const debouncedInputValue = useDebounce(value || searchValue, 500);
  const userInfo = useSelector(getUserInfo);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const router = useHistory();
  const containerRef = useRef<any>(null);

  const [isForm, setIsForm] = useState(false);

  const apiCallHandler = useCallback(
    (query: string) => {
      if (userInfo?.auth_token) {
        dispatch(getAuthSearchData(query));
      } else {
        dispatch(getSearchData(query));
      }
    },
    [dispatch, userInfo?.auth_token],
  );

  const onChangeHandler = useCallback(
    (e: any) => {
      if (e.target.value) {
        router?.push(`${pathname}?searchKeyword=${encodeURIComponent(e.target.value)}`);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
        router?.push(`${pathname}`);
        apiCallHandler("");
      }
      setValue(e.target.value);
      dispatch({
        type: SET_SEARCH_VAUE,
        payload: e.target.value,
      });
    },
    [dispatch, router, pathname, apiCallHandler],
  );

  const handleSubmit = (e: any) => {
    e.preventDefault();
    apiCallHandler(value);
    window.location.href = `${defaultSiteUrl}/feature-detail-page?searchKeyword=${encodeURIComponent(value)}`;
  };

  useEffect(() => {
    if (debouncedInputValue) {
      apiCallHandler(encodeURIComponent(debouncedInputValue));
    } else {
      dispatch({ type: CLEAR_SEARCH_DATA });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  function isReactRoute(targetRoute: string): boolean {
    let isReact = false;
    const reactRoutes = [
      "therapy-note",
      "toolbox/good-faith-estimate-generator",
      "toolbox/out-of-network-calculator",
      "toolbox/therapist-deductions-calculator",
      "toolbox/session-fee-calculator",
    ];

    reactRoutes.forEach((route: string) => {
      if (targetRoute.includes(route)) isReact = true;
    });
    // Check if the targetRoute exists in the reactRoutes
    return isReact;
  }

  const handleSearchClick = (route: string, refresh: boolean = true) => {
    if (refresh) {
      window.location.href = route;
    } else {
      router.push(route.replace(new RegExp(defaultSiteUrl, "g"), ""));
    }
    setShow(false);
  };

  useEffect(() => {
    if (isForm) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue(searchValue);
  }, [searchValue]);

  const getBoldTitle = (data: any) => {
    const newData = data
      .split(" ")
      .map((word: string) => {
        if (word.toLowerCase().includes(value?.toLowerCase())) {
          return `<strong>${word}</strong>`;
        }
        return word;
      })
      .join(" ");

    return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newData) }} />;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isScrollAvaialable = () => {
    if (containerRef?.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const isBottom = scrollTop + clientHeight >= scrollHeight;
      setShowScroll(!isBottom);
    }
  };

  useEffect(() => {
    const scrollContainer = containerRef?.current;
    isScrollAvaialable();
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", isScrollAvaialable);
    }
    return () => {
      scrollContainer?.removeEventListener("scroll", isScrollAvaialable);
    };
  }, [isScrollAvaialable, searchList, showSuggestions]);

  const handleScrollDown = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop += 300; // Adjust the value according to your requirement
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowNoResult(
        !searchList?.blogs?.data?.length &&
          !searchList?.resources?.data?.length &&
          !searchList?.tool_boxes?.data?.length &&
          !searchList?.forms?.data?.length &&
          !loader &&
          value,
      );
    }, 2000);

    return () => clearTimeout(timeout);
  }, [searchList, loader, value]);

  const onOuterIconClick = () => {
    // setShowSuggestions(true);
    setShow(true);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const onClear = () => {
    setValue("");
    setShow(false);
    router?.push(pathname);
    apiCallHandler("");
  };

  return (
    <>
      <div aria-hidden className="d-flex align-items-center me-2 me-sm-3" onClick={onOuterIconClick}>
        <i onClick={() => setShow(true)} aria-hidden className="fa-regular fa-search cursor-pointer" />
      </div>

      {show && (
        <div className="searchResult">
          <div aria-hidden className="searchOverlay" onClick={() => setShow(false)} />
          <div className="searchBox">
            {show && (
              <form onSubmit={handleSubmit}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search Blog, Resources, Practice Toolbox, Private Practice Forms"
                    value={value || searchValue}
                    onChange={onChangeHandler}
                    onFocus={() => setShowSuggestions(true)}
                    // onBlur={handleInputBlur}
                    ref={inputRef}
                  />
                  {value && (
                    <InputGroup.Text className="cursor-pointer" onClick={onClear}>
                      <i className="fa-solid fa-xmark" />
                    </InputGroup.Text>
                  )}
                  <Button variant="primary" className="btn-icon rounded-end" type="submit" disabled={!value}>
                    <i className="fa-regular fa-search" />
                  </Button>
                </InputGroup>
              </form>
            )}
            {showNoResult &&
              !loader &&
              !searchList?.blogs?.data?.length &&
              !searchList?.resources?.data?.length &&
              !searchList?.tool_boxes?.data?.length &&
              !searchList?.forms?.data?.length && <div className="mt-3">No Result Found</div>}
            {loader &&
              value &&
              !searchList?.blogs?.data?.length &&
              !searchList?.resources?.data?.length &&
              !searchList?.tool_boxes?.data?.length &&
              !searchList?.forms?.data?.length && (
                <div className="mt-3">
                  <i className="fa-solid fa-circle-notch fa-spin" /> Loading...
                </div>
              )}
            {!!(
              searchList?.blogs?.data?.length ||
              searchList?.resources?.data?.length ||
              searchList?.tool_boxes?.data?.length ||
              searchList?.forms?.data?.length
            ) &&
              showSuggestions &&
              (value || searchValue) && (
                <ul className="searchList" ref={containerRef}>
                  {searchList?.blogs?.data?.length > 0 &&
                    searchList.blogs?.data.slice(0, 5).map((blog: any) => (
                      <li className="cursor-pointer" aria-hidden key={blog.title} onClick={() => handleSearchClick(`${defaultSiteUrl}/blog/${blog.blog_slug}`)}>
                        <span className="text-blue cursor-pointer me-2 fw-bold">Blog</span>
                        <span className="cursor-pointer">{getBoldTitle(blog.title)}</span>
                      </li>
                    ))}
                  {searchList?.resources?.data?.length > 0 &&
                    searchList.resources?.data.slice(0, 5).map((resource: any) => (
                      <li
                        key={resource.name}
                        className="cursor-pointer"
                        aria-hidden
                        onClick={() => handleSearchClick(`${defaultSiteUrl}/resources/${resource?.id}/${resource?.url}`, false)}
                      >
                        <span className="text-blue cursor-pointer me-2 fw-bold">Resource</span>
                        <span className="cursor-pointer">{getBoldTitle(resource.name)}</span>
                      </li>
                    ))}
                  {searchList?.tool_boxes?.data?.length > 0 &&
                    searchList.tool_boxes?.data.slice(0, 5).map((toolbox: any) => (
                      <li
                        key={toolbox.title}
                        className="cursor-pointer"
                        aria-hidden
                        onClick={() => handleSearchClick(`${defaultSiteUrl}${toolbox.url}`, !isReactRoute(toolbox.url))}
                      >
                        <span className="text-blue cursor-pointer me-2 fw-bold">Toolbox</span>
                        <span className="cursor-pointer">{getBoldTitle(toolbox.title)}</span>
                      </li>
                    ))}
                  {searchList?.forms?.data?.length > 0 &&
                    searchList.forms?.data
                      .slice(0, 5)
                      .map((form: any) => (
                        <DropdownBlogListTitle
                          item={form}
                          key={form?.id}
                          title={getBoldTitle(form.name)}
                          toolName="Forms"
                          setShowSuggestions={setShowSuggestions}
                          setIsForm={setIsForm}
                          setShow={setShow}
                        />
                      ))}
                  <li aria-hidden className={`viewMore cursor-pointer ${showScroll ? "active" : ""}`} onClick={handleScrollDown}>
                    <i className="fa-regular fa-chevron-down cursor-pointer" />
                  </li>
                </ul>
              )}
          </div>
        </div>
      )}
    </>
  );
}

export default SearchFeature;
